import React, { useEffect, useState } from "react";
import { useInternalUsers } from "../../networking/internal-user.hooks";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import TableLayout from "../../components/Layout/TableLayout";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import Flex from "../../components/Layout/Flex";
import Button from "../../components/Core/Button/Button";
import AppInput from "../../components/Core/AppInput/AppInput";
import Pagination from "../../components/Shared/Pagination/Pagination";
import { PAGE_SIZE } from "../../configs/constants/Pagination";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import useDebounce from "../../hooks/useDebounce";
import EmptyState from "../../components/Shared/EmptyState/EmptyState";
import { formatRoute } from "../../utils/route.utils";
import { routes } from "../../configs/constants/routes";
import styled from "styled-components";
import { InternalUsersBreadcrumbs } from "../../configs/breadcrumbNavigation";

const UsersListPage: React.FC = () => {
	const [currentPage, setCurrentPage] = useState<number>(1);

	const { t } = useTranslation();

	const navigate = useNavigate();

	const [searchTerm, setSearchTerm] = useState<string>("");

	const debouncedSearchTerm = useDebounce(searchTerm, 500);

	// A new search always starts from the first page.
	useEffect(() => {
		setCurrentPage(1);
	}, [debouncedSearchTerm]);

	const { users, totalUserCount, isLoading } = useInternalUsers({
		page: currentPage,
		keyword: debouncedSearchTerm,
	});

	const getUsers = (e: any) => {
		setCurrentPage(e);
	};

	const navigateToUserDetails = (id?: string) => {
		const routePath = formatRoute(routes.INTERNAL_USER_PROFILE, [id]);
		navigate(routePath);
	};

	return (
		<>
			<Breadcrumbs items={InternalUsersBreadcrumbs} />
			<div className="p-4 px-6">
				<Flex justifyContent="space-between">
					<AppInput
						containerWidth="200px"
						placeholder={t("SEARCH")}
						icon="search"
						onChange={(e: any) => setSearchTerm(e.target.value)}
					/>
					<Link to={routes.INTERNAL_USERS_INVITE}>
						<Button width="9.75rem">{t("CREATE_USER")}</Button>
					</Link>
				</Flex>

				{isLoading ? (
					<LoadingIndicator />
				) : (
					<div>
						{users.length > 0 ? (
							<TableLayout>
								<table>
									<thead>
										<tr>
											<th>{t("FIRST_NAME")}</th>
											<th>{t("LAST_NAME")}</th>
											<th>{t("EMAIL")}</th>
											<th>{t("ROLE")}</th>
											<th>{t("STATUS")}</th>
										</tr>
									</thead>
									<tbody>
										{users
											.filter((user) => !user.isDeleted)
											.map((user) => {
												return (
													<TableRow
														key={user.id}
														onClick={() => navigateToUserDetails(user?.id)}
														className={!user.active ? "warning" : ""}
													>
														<td> {user.firstName}</td>
														<td> {user.lastName}</td>
														<td>{user.email}</td>
														<td>{t(`${user.roles ? user.roles[0].toUpperCase() : "-"}` as any)}</td>
														<td>{user.active ? t("ACTIVE") : t("INACTIVE")}</td>
													</TableRow>
												);
											})}
									</tbody>
								</table>
							</TableLayout>
						) : (
							<EmptyState label="NO_USERS" />
						)}
						{totalUserCount > PAGE_SIZE && (
							<Pagination
								currentPage={currentPage}
								totalCount={totalUserCount}
								onClickItem={(e) => getUsers(e)}
							/>
						)}
					</div>
				)}
			</div>
		</>
	);
};

const TableRow = styled.tr``;

export default UsersListPage;
